import React, { useCallback, useMemo } from 'react'
import {
  Button,
  BUTTON_SIZE,
  BUTTON_TYPE,
  DataType,
  InformationPanel,
  ReceiptCard,
  styled,
} from '~/app/components'
import { InformationFormat } from '~/app/components/order-list/details/InformationPanel'
import { isoToFormat } from '~/app/utils/useIsoToFormat'
import { usePgReceiptInfoQuery } from '~/__generated__/app/query/pgReceiptInfo/pgReceiptInfo.graphql'
import { useLoading } from '~/app/components/common/LoadingProvider'
import { getYaNative } from '@yanolja-rnd/ya-native'
import { toKRW } from '~/app/utils/krNumberToString'
import useErrorHandler from '~/app/utils/useErrorHandler'
import { isNative } from '~/app/utils/typeguards'
import { ApolloError } from '@apollo/client'

export type ReceiptInformation = {
  id: string
  createdAt: string
  title: string
  price: number
}
type ReceiptProps = {
  onFetchPgReceiptError: (error: ApolloError) => void
  information: ReceiptInformation
  hasPGPayment: boolean
}

const ButtonContainer = styled.div`
  display: flex;
  margin: 1.2rem 1rem 0;
  padding: 0 1rem;
  justify-content: space-between;

  & button {
    margin-right: 0.8rem;
    :nth-last-of-type {
      margin-right: 0;
    }
  }
`
const CASH_RECEIPT = 'CASH_RECEIPT'
const EXTERNAL_RECEIPT = 'EXTERNAL_RECEIPT'

const ReceiptItem: React.FC<ReceiptProps> = function ({
  information,
  hasPGPayment,
  onFetchPgReceiptError,
}) {
  const { data, error, loading } = usePgReceiptInfoQuery({
    variables: {
      tenancyCode: 'ORDER',
      orderId: information.id,
    },
    skip: !information.id || !hasPGPayment,
  })

  useLoading(loading)
  const { errorHandler } = useErrorHandler()
  errorHandler(error)

  const receiptUrl = useMemo(() => data?.pgReceiptInfo.receiptUrl || '', [data])

  const onButtonClick = useCallback(
    id => {
      if (id === CASH_RECEIPT) {
        // 현금영수증은 런칭 스펙에 미포함
      } else if (id === EXTERNAL_RECEIPT) {
        if (error) {
          onFetchPgReceiptError(error)
          return
        }

        const isCashReceipt = receiptUrl.includes(`/order-list/details/receipt/cash`)

        const yaNative = getYaNative()
        if (isNative(yaNative)) {
          yaNative.wv({
            method: 'get',
            url: receiptUrl,
            ...(!isCashReceipt && {
              layout: {
                title: '결제영수증',
                modal: false,
              },
            }),
          })
        } else {
          window.open(receiptUrl, '_blank')
        }
      }
    },
    [receiptUrl, error]
  )

  const receiptInformation = useMemo<Array<InformationFormat>>(() => {
    const { id, title, price, createdAt } = information
    const date = isoToFormat(createdAt, 'yyyy.MM.dd HH:mm')
    return [
      { type: DataType.TitleDescriptionSmall, title: '통합 주문번호', description: id },
      { type: DataType.TitleDescriptionSmall, title: '거래일시', description: date },
      { type: DataType.TitleDescriptionSmall, title: '상품명', description: title },
      { type: DataType.Dash },
      {
        type: DataType.EmphasizedDescription,
        title: '결제금액',
        description: toKRW(price),
      },
      { type: DataType.TitleDescriptionSmall, title: '회사명', description: '놀유니버스' },
    ]
  }, [information])

  return (
    <div>
      <ReceiptCard style={{ paddingTop: '1.2rem' }}>
        <InformationPanel information={receiptInformation} />
      </ReceiptCard>
      {hasPGPayment && (
        <ButtonContainer>
          <Button
            id={EXTERNAL_RECEIPT}
            title={'결제영수증 보기'}
            type={BUTTON_TYPE.BASIC}
            size={BUTTON_SIZE.M}
            onClick={onButtonClick}
            isFullSize
          />
        </ButtonContainer>
      )}
    </div>
  )
}

export default ReceiptItem
